import { PdfLayout } from '../PdfLayout';
import { Eye, GitBranch, ListChecks, Layers, Activity, RefreshCw } from 'lucide-react';

const benefits = [
  {
    icon: Eye,
    title: 'Familiar Ladder Editor',
    text: 'An editor modeled on Allen-Bradley CompactLogix: contacts, coils, timers, counters, compare, math, data-move and program-control instructions, with parallel branches for OR logic.',
  },
  {
    icon: GitBranch,
    title: 'Sequential Function Charts',
    text: 'Draw a sequence as steps and transitions — fill, heat, drain. Each transition is an ordinary ladder rung, and parallel and selection branches split and rejoin the flow.',
  },
  {
    icon: ListChecks,
    title: 'Step Actions',
    text: 'N, S, R and P qualifiers, plus timed L, D, SD, DS and SL, drive a tag or call a routine from each step. The active step is published as tags, ready for a dashboard.',
  },
  {
    icon: Layers,
    title: 'Tasks, Programs & Routines',
    text: 'Periodic or continuous tasks, a program per machine or station, and subroutines called with JSR — all reading and writing the same tags as the rest of the appliance.',
  },
  {
    icon: Activity,
    title: 'Live Monitor Mode & Forcing',
    text: 'Watch power flow rung by rung, with live values, accumulators and the active SFC step outlined. Force a bit on or off to commission an output — every force badged, listed and audited.',
  },
  {
    icon: RefreshCw,
    title: 'Edit From the Browser',
    text: 'Save and the change goes live — timers, counters and chart positions keep their state. Nothing to install on an engineering laptop.',
  },
];

export function LadderLogic() {
  return (
    <PdfLayout
      category="Automation"
      headline="Program Your Floor with"
      headlineHighlight="Ladder Logic & SFC"
      description="A soft-PLC built into the appliance for monitoring, data processing and non-safety automation. It runs without a real-time guarantee, so E-stops, guarding and other safety functions stay in dedicated safety hardware."
      accentColor="#3b82f6"
      benefits={benefits}
    >
      <div className="w-full max-w-[280px] space-y-3">
        <LadderMockup />
        <SfcMockup />
      </div>
    </PdfLayout>
  );
}

function WindowDots({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-2 mb-2 px-1">
      <div className="flex gap-1.5">
        <div className="w-2 h-2 rounded-full bg-status-fault/60" />
        <div className="w-2 h-2 rounded-full bg-status-idle/60" />
        <div className="w-2 h-2 rounded-full bg-status-running/60" />
      </div>
      <span className="text-[9px] text-text-muted">{title}</span>
    </div>
  );
}

function LadderMockup() {
  return (
    <div className="glass-panel p-4 w-full">
      <WindowDots title="Ladder Editor — Monitor" />

      <svg viewBox="0 0 240 160" className="w-full">
        {/* Power rails */}
        <line x1="20" y1="10" x2="20" y2="150" stroke="#3b82f6" strokeWidth="2" />
        <line x1="220" y1="10" x2="220" y2="150" stroke="#3b82f6" strokeWidth="2" />

        {/* Rung 1 */}
        <line x1="20" y1="40" x2="60" y2="40" stroke="#64748b" strokeWidth="1.5" />
        {/* NO Contact */}
        <line x1="60" y1="30" x2="60" y2="50" stroke="#3b82f6" strokeWidth="1.5" />
        <line x1="80" y1="30" x2="80" y2="50" stroke="#3b82f6" strokeWidth="1.5" />
        <text x="63" y="26" fontSize="7" fill="#3b82f6" fontWeight="600">Start</text>
        <line x1="80" y1="40" x2="120" y2="40" stroke="#64748b" strokeWidth="1.5" />
        {/* NC Contact */}
        <line x1="120" y1="30" x2="120" y2="50" stroke="#3b82f6" strokeWidth="1.5" />
        <line x1="140" y1="30" x2="140" y2="50" stroke="#3b82f6" strokeWidth="1.5" />
        <line x1="122" y1="50" x2="138" y2="30" stroke="#3b82f6" strokeWidth="1.5" />
        <text x="123" y="26" fontSize="7" fill="#3b82f6" fontWeight="600">Stop</text>
        <line x1="140" y1="40" x2="185" y2="40" stroke="#64748b" strokeWidth="1.5" />
        {/* Coil */}
        <circle cx="197" cy="40" r="12" fill="none" stroke="#16a34a" strokeWidth="1.5" />
        <text x="190" y="44" fontSize="8" fill="#16a34a" fontWeight="600">M1</text>
        <line x1="209" y1="40" x2="220" y2="40" stroke="#64748b" strokeWidth="1.5" />
        <text x="188" y="26" fontSize="7" fill="#16a34a">Motor</text>

        {/* Rung 2 */}
        <line x1="20" y1="85" x2="60" y2="85" stroke="#64748b" strokeWidth="1.5" />
        <line x1="60" y1="75" x2="60" y2="95" stroke="#7c3aed" strokeWidth="1.5" />
        <line x1="80" y1="75" x2="80" y2="95" stroke="#7c3aed" strokeWidth="1.5" />
        <text x="60" y="71" fontSize="7" fill="#7c3aed" fontWeight="600">Sensor</text>
        <line x1="80" y1="85" x2="185" y2="85" stroke="#64748b" strokeWidth="1.5" />
        <circle cx="197" cy="85" r="12" fill="none" stroke="#dc2626" strokeWidth="1.5" />
        <text x="190" y="89" fontSize="8" fill="#dc2626" fontWeight="600">A1</text>
        <line x1="209" y1="85" x2="220" y2="85" stroke="#64748b" strokeWidth="1.5" />
        <text x="186" y="71" fontSize="7" fill="#dc2626">Alarm</text>

        {/* Rung 3 */}
        <line x1="20" y1="130" x2="60" y2="130" stroke="#64748b" strokeWidth="1.5" />
        <line x1="60" y1="120" x2="60" y2="140" stroke="#06b6d4" strokeWidth="1.5" />
        <line x1="80" y1="120" x2="80" y2="140" stroke="#06b6d4" strokeWidth="1.5" />
        <text x="59" y="116" fontSize="7" fill="#06b6d4" fontWeight="600">Timer</text>
        <line x1="80" y1="130" x2="120" y2="130" stroke="#64748b" strokeWidth="1.5" />
        <line x1="120" y1="120" x2="120" y2="140" stroke="#06b6d4" strokeWidth="1.5" />
        <line x1="140" y1="120" x2="140" y2="140" stroke="#06b6d4" strokeWidth="1.5" />
        <text x="122" y="116" fontSize="7" fill="#06b6d4" fontWeight="600">Auto</text>
        <line x1="140" y1="130" x2="185" y2="130" stroke="#64748b" strokeWidth="1.5" />
        <circle cx="197" cy="130" r="12" fill="none" stroke="#ca8a04" strokeWidth="1.5" />
        <text x="191" y="134" fontSize="8" fill="#ca8a04" fontWeight="600">V1</text>
        <line x1="209" y1="130" x2="220" y2="130" stroke="#64748b" strokeWidth="1.5" />
        <text x="186" y="116" fontSize="7" fill="#ca8a04">Valve</text>

        {/* Status indicator */}
        <circle cx="30" cy="40" r="3" fill="#16a34a" />
        <circle cx="30" cy="85" r="3" fill="#64748b" />
        <circle cx="30" cy="130" r="3" fill="#16a34a" />
      </svg>

      <div className="flex items-center justify-between mt-2 px-1">
        <span className="text-[8px] text-text-muted">3 Rungs · Periodic 100 ms</span>
        <span className="text-[8px] text-status-running font-medium">● Running</span>
      </div>
    </div>
  );
}

function SfcMockup() {
  const step = (x: number, y: number, label: string, opts: { active?: boolean; initial?: boolean } = {}) => {
    const color = opts.active ? '#16a34a' : '#3b82f6';
    return (
      <g>
        <rect x={x - 26} y={y} width="52" height="15" rx="2" fill={color} fillOpacity="0.08" stroke={color} strokeWidth="1.3" />
        {opts.initial && <rect x={x - 23} y={y + 2.5} width="46" height="10" rx="1" fill="none" stroke={color} strokeWidth="0.8" />}
        <text x={x} y={y + 10} textAnchor="middle" fontSize="7" fill={color} fontWeight="600">{label}</text>
      </g>
    );
  };
  const transition = (x: number, y: number, label: string) => (
    <g>
      <line x1={x - 8} y1={y} x2={x + 8} y2={y} stroke="#475569" strokeWidth="2" />
      <text x={x + 12} y={y + 2.5} fontSize="6" fill="#64748b">{label}</text>
    </g>
  );
  const action = (x: number, y: number, q: string, tag: string) => (
    <g>
      <rect x={x} y={y} width="70" height="13" rx="2" fill="#ffffff" stroke="#cbd5e1" strokeWidth="0.8" />
      <line x1={x + 12} y1={y} x2={x + 12} y2={y + 13} stroke="#cbd5e1" strokeWidth="0.8" />
      <text x={x + 6} y={y + 9} textAnchor="middle" fontSize="6.5" fill="#7c3aed" fontWeight="700">{q}</text>
      <text x={x + 16} y={y + 9} fontSize="6" fill="#475569">{tag}</text>
    </g>
  );
  const wire = '#94a3b8';

  return (
    <div className="glass-panel p-4 w-full">
      <WindowDots title="SFC Chart — BatchCycle" />

      <svg viewBox="0 0 240 182" className="w-full">
        {/* Idle (initial) */}
        {step(95, 2, 'Idle', { initial: true })}
        <line x1="95" y1="17" x2="95" y2="31" stroke={wire} strokeWidth="1" />
        {transition(95, 24, 'XIC StartPB')}

        {/* Fill (active) */}
        {step(95, 31, 'Fill', { active: true })}
        <line x1="121" y1="38.5" x2="140" y2="38.5" stroke={wire} strokeWidth="0.8" />
        {action(140, 32, 'N', 'FillValve')}
        <line x1="95" y1="46" x2="95" y2="64" stroke={wire} strokeWidth="1" />
        {transition(95, 55, 'XIC LevelHigh')}

        {/* Parallel split */}
        <line x1="45" y1="64" x2="145" y2="64" stroke="#475569" strokeWidth="1" />
        <line x1="45" y1="67" x2="145" y2="67" stroke="#475569" strokeWidth="1" />
        <line x1="55" y1="67" x2="55" y2="74" stroke={wire} strokeWidth="1" />
        <line x1="135" y1="67" x2="135" y2="74" stroke={wire} strokeWidth="1" />

        {step(55, 74, 'Heat')}
        {step(135, 74, 'Agitate')}
        {action(162, 75, 'S', 'Agitator')}
        <line x1="55" y1="89" x2="55" y2="97" stroke={wire} strokeWidth="1" />
        <line x1="135" y1="89" x2="135" y2="97" stroke={wire} strokeWidth="1" />

        {/* Parallel join */}
        <line x1="45" y1="97" x2="145" y2="97" stroke="#475569" strokeWidth="1" />
        <line x1="45" y1="100" x2="145" y2="100" stroke="#475569" strokeWidth="1" />
        <line x1="95" y1="100" x2="95" y2="120" stroke={wire} strokeWidth="1" />
        {transition(95, 110, 'TON 5 min')}

        {/* Drain */}
        {step(95, 120, 'Drain')}
        <line x1="121" y1="127.5" x2="140" y2="127.5" stroke={wire} strokeWidth="0.8" />
        {action(140, 121, 'R', 'Agitator')}
        {action(140, 136, 'N', 'DrainValve')}
        <line x1="95" y1="135" x2="95" y2="165" stroke={wire} strokeWidth="1" />
        {transition(95, 157, 'XIO LevelLow')}

        {/* Loop back to Idle */}
        <line x1="95" y1="165" x2="18" y2="165" stroke={wire} strokeWidth="1" />
        <line x1="18" y1="165" x2="18" y2="9.5" stroke={wire} strokeWidth="1" />
        <line x1="18" y1="9.5" x2="69" y2="9.5" stroke={wire} strokeWidth="1" />
        <polygon points="65,6.5 70,9.5 65,12.5" fill={wire} />

        {/* Active step timer */}
        <text x="67" y="41" textAnchor="end" fontSize="6" fill="#16a34a">12 s</text>

        <text x="120" y="178" textAnchor="middle" fontSize="6" fill="#94a3b8">Each transition is a ladder rung</text>
      </svg>

      <div className="flex items-center justify-between mt-1 px-1">
        <span className="text-[8px] text-text-muted">5 Steps · Parallel branch</span>
        <span className="text-[8px] text-status-running font-medium">● Step: Fill</span>
      </div>
    </div>
  );
}
